import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Edit, Trash2, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StaffManagement = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'assistant'
  });
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/staff`);
      setStaff(response.data);
    } catch (error) {
      console.error('Error fetching staff:', error);
      alert('Failed to fetch staff members');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      let response;
      if (editingStaff) {
        response = await axios.put(`${API}/staff/${editingStaff.id}`, formData);
      } else {
        response = await axios.post(`${API}/staff`, formData);
      }
      
      console.log('Staff saved successfully:', response.data);
      
      // Refresh the staff list first
      await fetchStaff();
      
      // Then close modal and reset form
      setShowModal(false);
      resetForm();
      
      // Show success message
      alert(editingStaff ? 'Staff member updated successfully!' : 'Staff member added successfully!');
    } catch (error) {
      console.error('Error saving staff:', error);
      console.error('Error details:', error.response);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to save staff member';
      alert(`Error: ${errorMessage}`);
      // Keep modal open on error so user can retry
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = (staffMember) => {
    setEditingStaff(staffMember);
    setFormData({
      name: staffMember.name,
      email: staffMember.email,
      phone: staffMember.phone || '',
      role: staffMember.role
    });
    setShowModal(true);
  };

  const handleDelete = async (staffId) => {
    if (!window.confirm('Are you sure you want to delete this staff member?')) return;

    try {
      await axios.delete(`${API}/staff/${staffId}`);
      await fetchStaff();
    } catch (error) {
      console.error('Error deleting staff:', error);
      alert('Failed to delete staff member');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      role: 'assistant'
    });
    setEditingStaff(null);
  };

  const getRoleBadge = (role) => {
    const badges = {
      assistant: 'bg-blue-100 text-blue-800',
      junior_ca: 'bg-green-100 text-green-800',
      senior_ca: 'bg-purple-100 text-purple-800',
      manager: 'bg-orange-100 text-orange-800'
    };
    const labels = {
      assistant: 'Assistant',
      junior_ca: 'Junior CA',
      senior_ca: 'Senior CA',
      manager: 'Manager'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badges[role]}`}>
        {labels[role]}
      </span>
    );
  };

  const getRolePermissions = (role) => {
    const permissions = {
      assistant: ['View clients', 'Enter receipts', 'View tasks'],
      junior_ca: ['View clients', 'Edit tasks', 'Enter receipts'],
      senior_ca: ['Edit clients', 'Assign tasks', 'Enter receipts', 'Manage tasks'],
      manager: ['Edit clients', 'Assign tasks', 'Enter receipts', 'Manage tasks']
    };
    return permissions[role] || [];
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
            Staff Management
          </h2>
          <p className="text-gray-600 mt-2">Manage your team members and their permissions</p>
        </div>
        <Button 
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 shadow-lg hover:shadow-xl transition-all duration-200"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Staff Member
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 rounded-2xl">
          <CardContent className="p-8 text-center">
            <p className="text-blue-100 text-sm font-semibold uppercase tracking-wide mb-2">Total Staff</p>
            <div className="text-4xl font-black">{loading ? '...' : staff.length}</div>
            <div className="text-blue-100 text-sm opacity-90 mt-2">Team members</div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 rounded-2xl">
          <CardContent className="p-8 text-center">
            <p className="text-green-100 text-sm font-semibold uppercase tracking-wide mb-2">Active Staff</p>
            <div className="text-4xl font-black">{loading ? '...' : staff.filter(s => s.status === 'active').length}</div>
            <div className="text-green-100 text-sm opacity-90 mt-2">Currently active</div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-pink-600 text-white border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 rounded-2xl">
          <CardContent className="p-8 text-center">
            <p className="text-purple-100 text-sm font-semibold uppercase tracking-wide mb-2">Managers</p>
            <div className="text-4xl font-black">{loading ? '...' : staff.filter(s => s.role === 'manager').length}</div>
            <div className="text-purple-100 text-sm opacity-90 mt-2">Management team</div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-yellow-500 to-orange-600 text-white border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 rounded-2xl">
          <CardContent className="p-8 text-center">
            <p className="text-yellow-100 text-sm font-semibold uppercase tracking-wide mb-2">CAs</p>
            <div className="text-4xl font-black">{loading ? '...' : staff.filter(s => s.role.includes('ca')).length}</div>
            <div className="text-yellow-100 text-sm opacity-90 mt-2">Professional staff</div>
          </CardContent>
        </Card>
      </div>

      {/* Staff List */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="shadow-lg">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 space-y-2">
                    <div className="h-6 bg-gray-200 rounded w-3/4 animate-pulse"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse"></div>
                  </div>
                  <div className="h-6 bg-gray-200 rounded w-20 animate-pulse"></div>
                </div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                </div>
                <div className="flex gap-2">
                  <div className="h-10 bg-gray-200 rounded flex-1 animate-pulse"></div>
                  <div className="h-10 bg-gray-200 rounded w-16 animate-pulse"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : staff.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 px-4 text-center">
          <div className="w-32 h-32 rounded-3xl bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center mx-auto mb-8">
            <Users className="w-16 h-16 text-blue-600" />
          </div>
          <h3 className="text-3xl font-bold text-gray-900 mb-4">No Staff Members Yet</h3>
          <p className="text-lg text-gray-600 max-w-md mb-8 leading-relaxed">
            Add team members to delegate tasks, manage workload, and improve productivity.
          </p>
          <Button 
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold px-10 py-6 text-lg rounded-2xl shadow-lg hover:shadow-xl transition-all duration-200 h-14"
          >
            <Plus className="w-6 h-6 mr-2" />
            Add First Staff Member
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {staff.map((member) => (
            <Card key={member.id} className="bg-white shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 rounded-2xl border border-gray-200">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-1">{member.name}</h3>
                    <p className="text-sm text-gray-600">{member.email}</p>
                    {member.phone && (
                      <p className="text-sm text-gray-500 mt-1">{member.phone}</p>
                    )}
                  </div>
                  {getRoleBadge(member.role)}
                </div>

                <div className="mb-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Permissions</p>
                  <div className="flex flex-wrap gap-2">
                    {getRolePermissions(member.role).map((perm, idx) => (
                      <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                        {perm}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-red-600 font-semibold mt-2">
                    🔒 No access to total financials
                  </p>
                </div>

                <div className="flex gap-2 mt-4">
                  <Button
                    onClick={() => handleEdit(member)}
                    variant="outline"
                    className="flex-1 border-2 border-blue-200 text-blue-600 hover:bg-blue-50"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  <Button
                    onClick={() => handleDelete(member.id)}
                    variant="outline"
                    className="border-2 border-red-200 text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={(open) => {
        setShowModal(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {editingStaff ? 'Edit Staff Member' : 'Add Staff Member'}
            </DialogTitle>
            <DialogDescription>
              {editingStaff ? 'Update staff member details' : 'Add a new team member to your organization'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6 mt-4">
            <div>
              <Label htmlFor="name" className="text-sm font-semibold">Full Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                placeholder="John Doe"
                className="mt-2 h-12 focus-ring"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-sm font-semibold">Email Address *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                placeholder="john@example.com"
                className="mt-2 h-12 focus-ring"
              />
            </div>

            <div>
              <Label htmlFor="phone" className="text-sm font-semibold">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="9876543210"
                className="mt-2 h-12 focus-ring"
              />
            </div>

            <div>
              <Label htmlFor="role" className="text-sm font-semibold">Role *</Label>
              <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
                <SelectTrigger className="mt-2 h-12">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="assistant">Assistant (Basic access)</SelectItem>
                  <SelectItem value="junior_ca">Junior CA (Edit tasks)</SelectItem>
                  <SelectItem value="senior_ca">Senior CA (Assign tasks)</SelectItem>
                  <SelectItem value="manager">Manager (Full access except financials)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-2">
                All roles can enter fee collections but cannot view total financials
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="flex-1"
                disabled={formLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
                disabled={formLoading}
              >
                {formLoading ? 'Saving...' : (editingStaff ? 'Update' : 'Add Staff')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffManagement;
